import { FormEvent, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import { SUPPORT_EMAIL as supportEmail } from '../../lib/supportContact';

export default function AccountDeletionPage() {
  const [session, setSession] = useState<Session | null>(null);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const mailto = supportEmail
    ? `mailto:${supportEmail}?subject=${encodeURIComponent('Account deletion request')}`
    : '';

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const sendSignInLink = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const { error: signInError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}/account-deletion`,
        shouldCreateUser: false,
      },
    });

    if (signInError) {
      setError('We could not send the secure sign-in link. Check the email and try again.');
    } else {
      setMessage('Check your email and open the secure link to continue your deletion request.');
    }
    setLoading(false);
  };

  const submitDeletionRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!session?.user) return;

    setLoading(true);
    setError('');
    setMessage('');

    // Delete for real: the delete-user function de-identifies the account, revokes
    // its credentials and signs out every session.
    const { data: result, error: fnError } = await supabase.functions.invoke('delete-user', {
      body: { userId: session.user.id },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!fnError && result?.success) {
      await supabase.auth.signOut().catch(() => undefined);
      setMessage('Your account has been deleted and you have been signed out. Records that must be kept by law are retained without your personal details.');
      setReason('');
      setLoading(false);
      return;
    }

    // Fallback: record the request so it is never lost.
    const { error: requestError } = await supabase.from('account_deletion_requests').insert({
      user_id: session.user.id,
      email: session.user.email ?? null,
      reason: reason.trim() || 'Immediate deletion unavailable',
      status: 'pending',
    });

    if (requestError?.code === '23505') {
      setMessage('A deletion request for this account is already pending review.');
    } else if (requestError) {
      setError('The deletion request could not be submitted. Please try again or email support.');
    } else {
      setMessage('Your account deletion request was recorded. We will complete it within 30 days.');
      setReason('');
    }
    setLoading(false);
  };

  return (
    <Container dir="ltr" maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 1, textAlign: 'left' }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              Request account deletion
            </Typography>
            <Typography color="text.secondary">
              Makeen Real Estate Management lets signed-in users request deletion from
              Profile &gt; Delete Account. You can also verify your account and submit the
              request securely from this page.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              How to request deletion
            </Typography>
            <Typography component="ol" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li>Open the Makeen app and sign in.</li>
              <li>Go to Profile.</li>
              <li>Tap Delete Account.</li>
              <li>Confirm the deletion request.</li>
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              If you cannot access the app, use the email address on your Makeen account below.
              We will send a secure link before accepting the request.
            </Typography>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {message ? <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert> : null}
            {session?.user ? (
              <Box component="form" onSubmit={submitDeletionRequest}>
                <Typography sx={{ mb: 2 }}>
                  Verified account: {session.user.email}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Reason (optional)"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  inputProps={{ maxLength: 1000 }}
                />
                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2 }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Delete my account'}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={sendSignInLink}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  autoComplete="email"
                  label="Account email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2 }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Send secure link'}
                </Button>
              </Box>
            )}
            {supportEmail ? (
              <Button href={mailto} variant="text" sx={{ mt: 2 }}>
                Email support
              </Button>
            ) : null}
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              What is deleted
            </Typography>
            <Typography color="text.secondary">
              When you confirm, your account profile, sign-in credentials, profile photo,
              contact details, and personal settings are deleted immediately and every
              session is signed out.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              What may be retained
            </Typography>
            <Typography color="text.secondary">
              Some property, contract, invoice, payment, tax, accounting, security, and dispute
              records may be retained when required for legal, financial, fraud-prevention, or
              business-record obligations. Retained records are limited to what is required and are
              no longer used as an active app account.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Timing
            </Typography>
            <Typography color="text.secondary">
              Deletion from the app or from this page completes immediately. Requests sent by
              email are completed within 30 days. If a record must be retained, we keep it only
              for the required retention period.
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            See also <Link href="/privacy">Privacy Policy</Link> and{' '}
            <Link href="/terms">Terms of Service</Link>, or return to{' '}
            <Link href="/auth/login">Makeen sign in</Link>.
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
